import React from 'react';
import { Link } from 'react-router-dom';
import { Image, Badge, Button, Overlay, Popover, Form } from 'react-bootstrap';
import PropTypes, { element } from 'prop-types';
import Icofont from 'react-icofont';
import ItemsCarousel from './ItemsCarousel';
import ProductBox from '../home/ProductBox';
import QuickBite from './QuickBite';
import { formatProductDisplay } from '../../utils.js';

// Detect pizza size keyword in a Zusatz name or Größe label so we can
// group per-size DB rows (e.g. "A1. Käse -STANDARD", "A1. Käse- JUMBO",
// "A1. Käse - PARTY") under one UI entry and auto-match the current size.
const detectSizeKey = (text) => {
  if (!text) return null;
  const t = String(text);
  if (/PARTY|50\s*CM/i.test(t)) return 'PARTY';
  if (/JUMBO|36\s*CM/i.test(t)) return 'JUMBO';
  if (/STANDARD|26\s*CM/i.test(t)) return 'STANDARD';
  return null;
};

const stripSizeMarkers = (name) => {
  if (!name) return '';
  return String(name)
    .replace(/\bSTANDARD\b|\bJUMBO\b|\bPARTY\b/gi, '')
    .replace(/\(\s*ø\s*\d+\s*cm\s*\)/gi, '')
    .replace(/ø\s*\d+\s*cm/gi, '')
    .replace(/\b\d+\s*cm\b/gi, '')
    .replace(/\s+/g, ' ')
    .replace(/^[\s\-–—]+|[\s\-–—]+$/g, '')
    .trim();
};

const sizeKeyForGroesse = (groesse) => {
  if (!groesse) return null;
  return detectSizeKey(groesse.groesse)
    || detectSizeKey(groesse.einheit)
    || (groesse.id != null ? `SIZE_${groesse.id}` : null);
};

// Build UI-friendly groups of Zusätze. Handles two admin data patterns:
// 1) Names carry the size keyword ("Standard mit Mozzarella-Rand", "Jumbo …"):
//    grouped by stripped base name, each variant indexed by its own sizeKey.
// 2) Names are identical per size ("A1. Käse" ×3 with prices 2/4/6):
//    auto-matched to Größen by ascending price.
const buildZusatzGroups = (zusaetze, groessen) => {
  const groups = new Map();
  (zusaetze || []).forEach(z => {
    const stripped = stripSizeMarkers(z.name);
    const baseName = stripped || z.name;
    const sizeKey = detectSizeKey(z.name);
    if (!groups.has(baseName)) groups.set(baseName, { baseName, variants: {}, unkeyed: [] });
    const g = groups.get(baseName);
    if (sizeKey) g.variants[sizeKey] = z;
    else g.unkeyed.push(z);
  });

  const sortedGroessen = (groessen || []).slice().sort(
    (a, b) => parseFloat(a.preis) - parseFloat(b.preis)
  );

  groups.forEach(g => {
    if (g.unkeyed.length === 0) {
      // all variants already keyed by detected size → nothing to do
    } else if (sortedGroessen.length > 0 && g.unkeyed.length === sortedGroessen.length) {
      // auto-match: cheapest Zusatz → smallest Größe, and so on
      const sortedZ = g.unkeyed.slice().sort(
        (a, b) => parseFloat(a.preis) - parseFloat(b.preis)
      );
      sortedZ.forEach((z, i) => {
        const k = sizeKeyForGroesse(sortedGroessen[i]);
        if (k) g.variants[k] = z;
      });
    } else if (g.unkeyed.length === 1) {
      g.variants.universal = g.unkeyed[0];
    } else {
      // count mismatch, can't map reliably → expose each variant separately
      // so no pricing info is lost
      g.variants.universal = g.unkeyed[0];
    }
    delete g.unkeyed;
  });

  return Array.from(groups.values());
};

// Split flat Zusatz list into category buckets and apply size-grouping
// within each bucket. The optional `configs` array (partner.zusatz_kategorien)
// drives display order and Pflichtfeld/Wunschfeld status. Categories without a
// config fall back to: named → required, unnamed → optional.
const buildZusatzKategorien = (zusaetze, groessen, configs) => {
  const byKategorie = new Map();
  (zusaetze || []).forEach(z => {
    const cat = (z.kategorie || '').trim();
    if (!byKategorie.has(cat)) byKategorie.set(cat, []);
    byKategorie.get(cat).push(z);
  });

  // Index configs by kategorie name (case-insensitive) for quick lookup.
  const configByName = new Map();
  (configs || []).forEach(c => {
    const key = (c.name || '').trim().toLowerCase();
    if (key) configByName.set(key, c);
  });

  const kategorien = [];
  byKategorie.forEach((items, kategorie) => {
    const cfg = kategorie ? configByName.get(kategorie.toLowerCase()) : null;
    kategorien.push({
      kategorie,
      groups: buildZusatzGroups(items, groessen),
      config: cfg || null,
      // Explicit config wins; otherwise use the historical default
      // (named categories are required, unnamed is optional).
      isRequired: cfg ? !!cfg.is_required : !!kategorie,
      sortOrder: cfg ? cfg.sort_order : (kategorie ? 500 : 999),
    });
  });
  // Order by config-driven sort_order first (unconfigured groups go to a
  // sensible middle position), then alphabetically as a tie-breaker.
  kategorien.sort((a, b) => {
    if (a.sortOrder !== b.sortOrder) return a.sortOrder - b.sortOrder;
    return (a.kategorie || '').localeCompare(b.kategorie || '');
  });
  return kategorien;
};

const findGroupForVariant = (kategorien, zusatzId) => {
  for (const kat of (kategorien || [])) {
    for (const g of (kat.groups || [])) {
      if (Object.values(g.variants).some(v => v.id === zusatzId)) return g;
    }
  }
  return null;
};

const pickVariantForSize = (group, currentSizeKey) => {
  if (!group) return null;
  if (currentSizeKey && group.variants[currentSizeKey]) return group.variants[currentSizeKey];
  if (group.variants.universal) return group.variants.universal;
  // Fallback: no detected current size or no matching variant — show the first
  // available so the Zusatz isn't accidentally hidden.
  const keys = Object.keys(group.variants);
  if (keys.length > 0) return group.variants[keys[0]];
  return null;
};

class BestSeller extends React.Component {
  constructor(props) {
    super(props);
    
    const savedData = JSON.parse(localStorage.getItem(`restaurant-products-${this.props.restaurantInfo.id}`) || "{}");
    let qty = this.props.qty || 0;
    this.loadExtras = this.loadExtras.bind(this);
    
    if (savedData[this.props.title]) {
      qty = savedData[this.props.title].amount;
    }

    const groessen = this.props.product.groessen || [];
    const zusaetze = this.props.product.zusaetze || [];
    const zusaetzeKategorien = buildZusatzKategorien(
      zusaetze,
      groessen,
      this.props.restaurantInfo?.zusatz_kategorien
    );
    const savedProduct = savedData[this.props.title];
    const selectedGroesse = savedProduct?.selectedGroesse || (groessen.length > 0 ? groessen[0] : null);

    this.state = {
      quantity: qty,
      show: this.props.show || true,
      max: this.props.maxValue || 40,
      min: this.props.minValue || 0,
      showOverlay: false,
      showExtra: false,
      infoButtonRef: React.createRef(),
      extrasButtonRef: React.createRef(),
      warnings: [],
      extras: [],
      groessen: groessen,
      zusaetze: zusaetze,
      zusaetzeKategorien: zusaetzeKategorien,
      selectedGroesse: selectedGroesse,
      selectedZusaetze: savedProduct?.selectedZusaetze || []
    };

    if (savedData[this.props.title]) {
      this.props.checkOutItem.current.setFunctions(this.props.itemsCarousel.onProductAdd, this.props.itemsCarousel.onProductRemove);
      this.props.checkOutItem.current.setProductItem(this, qty);
    }

    this.loadExtras();
  }

  getPrice() {
    if (this.state.selectedGroesse) {
      return parseFloat(this.state.selectedGroesse.preis) || this.props.price;
    }
    return this.props.price;
  }

  getPricePfand() {
    return this.props.pricePfand;
  }

  IncrementItem = () => {
    if (this.state.quantity >= this.state.max) {

    } else {
      this.props.onAdd(this);
    }
  }
  DecreaseItem = () => {
    if (this.state.quantity <= (this.state.min)) {
      
    } else {
      this.props.onRemove(this);
      this.setState({ showExtra: false });
    }
  }

  addItem() {
    this.setState({
      quantity: this.state.quantity + 1
    });

    this.props.getValue({ id: this.props.id, quantity: (this.state.quantity + 1) });
  }

  removeItem() {
    this.setState({ quantity: this.state.quantity - 1 });
    this.props.getValue({ id: this.props.id, quantity: (this.state.quantity - 1) });
  }

  handleOverlayToggle = () => {
    const state = this.state;
    state.showOverlay = !state.showOverlay

    if (state.showOverlay) {
      this.props.product.inhaltsstoffe.forEach(element => {
        state.warnings.push((
          <p className="zusatzstoffe">{element.name} - {element.text}</p>
        ));
      });
    } else {
      state.warnings = [];
    }

    this.setState(state);
  };

  handleExtraToogle = () => {
    const state = this.state;
    state.showExtra = !state.showExtra

    this.setState(state);
  };

  onGroesseChange = (e) => {
    const groesseId = parseInt(e.target.value);
    const groesse = this.state.groessen.find(g => g.id === groesseId);

    // When size changes, remap selected Zusätze to the matching variant of the
    // new size. If a group has no variant for the new size, that selection is
    // dropped (no valid pricing available).
    const newSizeKey = sizeKeyForGroesse(groesse);
    const remappedZusaetze = (this.state.selectedZusaetze || [])
      .map(z => {
        const group = findGroupForVariant(this.state.zusaetzeKategorien, z.id);
        if (!group) return z;
        return pickVariantForSize(group, newSizeKey);
      })
      .filter(Boolean);

    this.setState({ selectedGroesse: groesse, selectedZusaetze: remappedZusaetze });

    const products = JSON.parse(localStorage.getItem(`restaurant-products-${this.props.restaurantInfo.id}`) || "{}");
    if (products[this.props.title]) {
      products[this.props.title].selectedGroesse = groesse;
      products[this.props.title].price = parseFloat(groesse.preis);
      products[this.props.title].selectedZusaetze = remappedZusaetze;
      localStorage.setItem(`restaurant-products-${this.props.restaurantInfo.id}`, JSON.stringify(products));
    }

    if (this.props.checkOutItem?.current?.loadExtras) {
      this.props.checkOutItem.current.loadExtras(this);
    }
  };

  onZusatzToggle = (zusatz) => {
    let selected = [...this.state.selectedZusaetze];
    const idx = selected.findIndex(z => z.id === zusatz.id);
    if (idx >= 0) {
      selected.splice(idx, 1);
    } else {
      selected.push(zusatz);
    }
    this.persistZusatzSelection(selected);
  };

  // Radio-style picker for a named Kategorie. Replaces any other selection
  // from the same Kategorie so the customer ends up with exactly one choice
  // per group (Lieferando-style).
  onZusatzKategorieSelect = (kategorie, zusatz) => {
    const selected = this.state.selectedZusaetze.filter(
      z => (z.kategorie || '').trim() !== kategorie
    );
    selected.push(zusatz);
    this.persistZusatzSelection(selected);
  };

  persistZusatzSelection = (selected) => {
    this.setState({ selectedZusaetze: selected });

    const products = JSON.parse(localStorage.getItem(`restaurant-products-${this.props.restaurantInfo.id}`) || "{}");
    if (products[this.props.title]) {
      products[this.props.title].selectedZusaetze = selected;
      localStorage.setItem(`restaurant-products-${this.props.restaurantInfo.id}`, JSON.stringify(products));
    }

    if (this.props.checkOutItem?.current?.loadExtras) {
      this.props.checkOutItem.current.loadExtras(this);
    }
  };

  loadExtras() {
    const extras = this.props.product.extras || [];
    const state = this.state;

    let extraAmount = {};

    const lastRestaurant = JSON.parse(localStorage.getItem("last-restaurant") || "{}");
    if (Object.keys(lastRestaurant).length !== 0) {
      const products = JSON.parse(localStorage.getItem(`restaurant-products-${lastRestaurant.id}`) || "{}");
      if (Object.keys(products).length !== 0) {
        if (products[this.props.title]) {
          products[this.props.title].extras.forEach(element => {
            extraAmount[element.name] = element.amount;
          });
        }
      }

    }

    state.extras = [];

    extras.forEach(element => {
      state.extras.push((
        <QuickBite
          id={element.id}
          title={element.name}
          price={element.price}
          qty={extraAmount[element.name] || 0}
          priceUnit='€'
          getValue={(data) => this.props.getExtras(this, data, element)}
        />
      ));
    });

    this.setState(state);
  }

  render() {
    return (
      <div className="list-card bg-white h-100 rounded overflow-hidden position-relative shadow-sm d-flex flex-column">
      {/* <div className="list-card bg-white h-100 rounded overflow-hidden position-relative shadow-sm"></div>  */}
        <div className="list-card-image">
        {this.state.quantity > 0 && this.state.extras.length !== 0 ? (
          <Button ref={this.state.extrasButtonRef} className="position-absolute bottom-10 z-index-100" variant="light" onClick={this.handleExtraToogle}>Extras</Button>
        ) : ""}
          {this.props.rating ? (
            <div className="star position-absolute">
              <Badge variant="success">
                <Icofont icon='star' /> {this.props.rating}
              </Badge>
            </div>) : ""}
          {/* neu taste info mit inhaltstoffe */}
          <div className="member-plan position-absolute z-index-100">
            <Button ref={this.state.infoButtonRef} variant="light" onClick={this.handleOverlayToggle}>
              <Icofont icon="info-circle" size="lg" style={{ color: '#3ecf8e' }} />
            </Button>
          </div>
          {
            this.props.showPromoted ? (
              <div className="member-plan position-absolute">

                <Badge variant={this.props.promotedVariant}>Promoted</Badge>
              </div>
            ) : ""
          }
          <div>
            <Image src={this.props.image} className={this.props.imageClass} alt={this.props.imageAlt} />
          </div>
        </div>
        <div className="p-3 position-relative flex-fill d-flex flex-column justify-content-between">
          <div className="list-card-body">
            <h6 className="mb-1 text-black">{formatProductDisplay(this.props.title, this.props.product?.nummer)}</h6>
            {this.props.subTitle ? (
              <p className="text-gray pb-3 text-1rem">{this.props.subTitle}</p>
              // <p className="text-gray mb-3 ">{this.props.subTitle}</p>
            )
              : ''
            }
            {this.state.groessen.length > 0 ? (
              <Form.Group className="mb-2">
                <Form.Control as="select" size="sm" value={this.state.selectedGroesse?.id || ''} onChange={this.onGroesseChange}>
                  {this.state.groessen.map(g => (
                    <option key={g.id} value={g.id}>{g.groesse}{g.einheit ? ` (${g.einheit})` : ''} - {parseFloat(g.preis).toFixed(2)} €</option>
                  ))}
                </Form.Control>
              </Form.Group>
            ) : ''}
            {this.state.zusaetzeKategorien && this.state.zusaetzeKategorien.length > 0 ? (() => {
              const currentSizeKey = sizeKeyForGroesse(this.state.selectedGroesse);
              return this.state.zusaetzeKategorien.map(({ kategorie, groups, isRequired }) => {
                const rows = groups
                  .map(g => ({ group: g, variant: pickVariantForSize(g, currentSizeKey) }))
                  .filter(r => r.variant);
                if (rows.length === 0) return null;

                const label = kategorie || 'Zusätze';
                const fieldTag = isRequired ? '(Pflichtfeld)' : '(Wunschfeld)';
                const key = kategorie ? `cat-${kategorie}` : 'cat-default';

                if (isRequired) {
                  const radioName = `zusatz-${this.props.id}-${kategorie || 'default'}`;
                  return (
                    <div key={key} className="zusaetze-inline mb-2">
                      <p className="mb-1 text-gray small">
                        <strong>{label}:</strong> <span className="text-muted">{fieldTag}</span>
                      </p>
                      {rows.map(({ group, variant }) => (
                        <Form.Check
                          key={group.baseName}
                          type="radio"
                          name={radioName}
                          id={`${radioName}-${variant.id}`}
                          label={`${group.baseName} (+${parseFloat(variant.preis).toFixed(2)} €)`}
                          checked={this.state.selectedZusaetze.some(s => s.id === variant.id)}
                          onChange={() => this.onZusatzKategorieSelect(kategorie || '', variant)}
                        />
                      ))}
                    </div>
                  );
                }

                return (
                  <div key={key} className="zusaetze-inline mb-2">
                    <p className="mb-1 text-gray small">
                      <strong>{label}:</strong> <span className="text-muted">{fieldTag}</span>
                    </p>
                    {rows.map(({ group, variant }) => (
                      <Form.Check
                        key={group.baseName}
                        type="checkbox"
                        id={`zusatz-${this.props.id}-${variant.id}`}
                        label={`${group.baseName} (+${parseFloat(variant.preis).toFixed(2)} €)`}
                        checked={this.state.selectedZusaetze.some(s => s.id === variant.id)}
                        onChange={() => this.onZusatzToggle(variant)}
                      />
                    ))}
                  </div>
                );
              });
            })() : ''}
            {/* Pfand бутылка */}
            {this.props.pricePfand != 0 ? (
              <p className="text-gray mb-3 text-1rem">zzgl. Pfand {this.props.pricePfand} €</p>
            )
              : ''
            }
           {/*  {(this.props.price) ? (
              <div className="text-gray time mb-0">
                <div className="pl-0 text-black pr-0 d-inline-block">
                  {this.props.price} {this.props.priceUnit}
                </div>
                {(this.props.isNew) ? (<Badge variant="success" className='ml-1'>NEW</Badge>) : ""}

                {this.state.quantity === 0 ?
                  <span className="float-right">
                    <Button variant='outline-secondary' onClick={this.IncrementItem} size="sm">ADD</Button>
                  </span>
                  :
                  <span className="count-number float-right">
                    <Button variant="outline-secondary" onClick={this.DecreaseItem} className="btn-sm left dec"> <Icofont icon="minus" /> </Button>
                    <input className="count-number-input" type="text" value={this.state.quantity} readOnly />
                    <Button variant="outline-secondary" onClick={this.IncrementItem} className="btn-sm right inc"> <Icofont icon="icofont-plus" /> </Button>
                  </span>
                }
              </div>
            ) : ''
            } */}

            

          </div>
          {(this.props.price != null || this.state.selectedGroesse) ? (
              <div className="text-gray time mb-0 d-flex align-items-center justify-content-between">
              {/* <div className="text-gray time mb-0"> */}
                <div className="pl-0 text-black pr-0 d-inline-block">
                  {this.getPrice().toFixed(2)} {this.props.priceUnit}
                </div>
                {(this.props.isNew) ? (<Badge variant="success" className='ml-1'>NEW</Badge>) : ""}

                {this.state.quantity === 0 ?
                  <span className="float-right">
                    <Button variant='outline-secondary' onClick={this.IncrementItem} size="sm">ADD</Button>
                  </span>
                  :
                  <span className="count-number float-right">
                    <Button variant="outline-secondary" onClick={this.DecreaseItem} className="btn-sm left dec"> <Icofont icon="minus" /> </Button>
                    <input className="count-number-input" type="text" value={this.state.quantity} readOnly />
                    <Button variant="outline-secondary" onClick={this.IncrementItem} className="btn-sm right inc"> <Icofont icon="icofont-plus" /> </Button>
                  </span>
                }
              </div>
            ) : ''
            }
        </div>


        {/* <Button ref={this.state.infoButtonRef} variant="outline-info" onClick={this.handleOverlayToggle}>
          <Icofont icon="info-circle" size="lg" />
        </Button> */}


        <Overlay target={this.state.infoButtonRef.current} show={this.state.showOverlay} placement="bottom">
          {(props) => (
            <Popover {...props}>
              <Popover.Title as="h3">{props.title}</Popover.Title>
              <Popover.Content>
                {/*  <p>Info:</p>
                <p>{this.props.product.info}</p> */}

                <p style={{ lineHeight: "1.3em" }}>Enthaltene Zusatzstoffe und Allergene:</p>
                {this.state.warnings}
              </Popover.Content>
            </Popover>
          )}
        </Overlay>

        <Overlay target={this.state.extrasButtonRef.current} show={this.state.showExtra} placement="bottom">
          <Popover>
            <Popover.Title as="h3"></Popover.Title>
            <Popover.Content>
              {this.state.extras}
            </Popover.Content>
          </Popover>
        </Overlay>

        {/* {this.state.quantity > 0 && this.state.extras.length !== 0 ? (
          <Button ref={this.state.extrasButtonRef} variant="light" onClick={this.handleExtraToogle}>Extras</Button>
        ) : ""} */}
        
      </div>
    );
  }
}

BestSeller.propTypes = {
  product: PropTypes.any.isRequired,
  title: PropTypes.string.isRequired,
  imageAlt: PropTypes.string,
  image: PropTypes.string.isRequired,
  imageClass: PropTypes.string,
  isNew: PropTypes.bool,
  subTitle: PropTypes.string,
  price: PropTypes.number.isRequired,
  pricePfand: PropTypes.number.isRequired,
  priceUnit: PropTypes.string.isRequired,
  showPromoted: PropTypes.bool,
  promotedVariant: PropTypes.string,
  favIcoIconColor: PropTypes.string,
  rating: PropTypes.string,
  id: PropTypes.number.isRequired,
  qty: PropTypes.number,
  minValue: PropTypes.number,
  maxValue: PropTypes.number,
  getValue: PropTypes.func.isRequired,
  onAdd: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  restaurantInfo: PropTypes.any.isRequired,
  checkOutItem: PropTypes.any.isRequired,
  itemsCarousel: PropTypes.any.isRequired,
  getExtras: PropTypes.func.isRequired,
  subCategory: PropTypes.any,
};

BestSeller.defaultProps = {
  imageAlt: '',
  imageClass: '',
  isNew: false,
  subTitle: '',
  price: '',
  priceUnit: '€',
  showPromoted: false,
  promotedVariant: 'dark',
  favIcoIconColor: '',
  rating: '',
  pricePfand: ''
}

export default BestSeller;